import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { usePOSSettings } from '@/hooks/usePOSSettings';
import { usePro } from '@/hooks/usePro';
import { Transaction, saveTransaction, loadTransactions, Product, loadProducts, saveProducts, Coupon, loadCoupons } from '@/lib/posStorage';
import { POSSettings } from '@/lib/posStorage';
import { track } from '@/lib/analytics';

export type POSView = 'checkout' | 'discount' | 'tip' | 'payment' | 'success' | 'history' | 'settings' | 'menu-manage' | 'upgrade' | 'invoices' | 'dashboard' | 'cashstamps';

/** Which asset the current sale is being settled in. */
export type PayAsset = 'BCH' | 'PUSD';

export interface CartItem {
  product: Product;
  qty: number;
}

interface POSContextType {
  view: POSView;
  setView: (v: POSView) => void;
  // Subtotal (pre-tax, pre-tip)
  fiatAmount: number;
  setFiatAmount: (n: number) => void;
  // Discount
  discountAmount: number;
  discountLabel: string;
  setDiscount: (amount: number, label: string) => void;
  coupons: Coupon[];
  refreshCoupons: () => void;
  // Tip
  tipAmount: number;
  setTipAmount: (n: number) => void;
  // Note / memo
  note: string;
  setNote: (s: string) => void;
  // BCH amount for pending payment (tax + tip inclusive)
  pendingBCH: number;
  setPendingBCH: (n: number) => void;
  // Which asset the current sale is being paid in (BCH default)
  payWith: PayAsset;
  setPayWith: (a: PayAsset) => void;
  lastTransaction: Transaction | null;
  transactions: Transaction[];
  addTransaction: (tx: Transaction) => void;
  settings: POSSettings;
  setSettings: (s: POSSettings) => void;
  updateSettings: (partial: Partial<POSSettings>) => void;
  products: Product[];
  refreshProducts: () => void;
  cart: CartItem[];
  cartTotal: number;
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  updateCartQty: (productId: string, qty: number) => void;
  clearCart: () => void;
  isPro: boolean;
  activatePro: (txid: string) => void;
}

const POSContext = createContext<POSContextType | undefined>(undefined);

export function POSProvider({ children }: { children: ReactNode }) {
  const [view, setView] = useState<POSView>(
    () => window.location.hash === '#settings' ? 'settings' : 'checkout'
  );
  const [fiatAmount, setFiatAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountLabel, setDiscountLabel] = useState('');
  const [tipAmount, setTipAmount] = useState(0);
  const [note, setNote] = useState('');

  const [coupons, setCoupons] = useState<Coupon[]>(() => loadCoupons());
  const refreshCoupons = useCallback(() => setCoupons(loadCoupons()), []);

  const setDiscount = useCallback((amount: number, label: string) => {
    setDiscountAmount(amount);
    setDiscountLabel(label);
  }, []);
  const [pendingBCH, setPendingBCH] = useState(0);
  const [payWith, setPayWith] = useState<PayAsset>('BCH');
  const [lastTransaction, setLastTransaction] = useState<Transaction | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>(() => loadTransactions());
  const { settings, setSettings, updateSettings } = usePOSSettings();
  const { isPro, activatePro } = usePro();

  const [products, setProducts] = useState<Product[]>(() => loadProducts());
  const refreshProducts = useCallback(() => setProducts(loadProducts()), []);

  const [cart, setCart] = useState<CartItem[]>([]);
  const cartTotal = cart.reduce((sum, item) => sum + item.product.price * item.qty, 0);

  const addToCart = useCallback((product: Product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) return prev.map((i) => i.product.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { product, qty: 1 }];
    });
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart((prev) => prev.filter((i) => i.product.id !== productId));
  }, []);

  const updateCartQty = useCallback((productId: string, qty: number) => {
    if (qty <= 0) setCart((prev) => prev.filter((i) => i.product.id !== productId));
    else setCart((prev) => prev.map((i) => i.product.id === productId ? { ...i, qty } : i));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  void saveProducts;

  const addTransaction = useCallback((tx: Transaction) => {
    saveTransaction(tx);
    setTransactions(loadTransactions());
    setLastTransaction(tx);

    // Record a completed payment for analytics (anonymous, best-effort).
    track({
      type: 'payment',
      fiatAmount: tx.fiatAmount,
      bchAmount: tx.bchAmount,
      tipAmount: tx.tipAmount,
      currency: tx.currency,
      offline: tx.txid.startsWith('offline-'),
      itemCount: tx.items?.length ?? 0,
    });
  }, []);

  void loadCoupons;

  return (
    <POSContext.Provider value={{
      view, setView,
      fiatAmount, setFiatAmount,
      discountAmount, discountLabel, setDiscount,
      coupons, refreshCoupons,
      tipAmount, setTipAmount,
      note, setNote,
      pendingBCH, setPendingBCH,
      payWith, setPayWith,
      lastTransaction, transactions, addTransaction,
      settings, setSettings, updateSettings,
      products, refreshProducts,
      cart, cartTotal, addToCart, removeFromCart, updateCartQty, clearCart,
      isPro, activatePro,
    }}>
      {children}
    </POSContext.Provider>
  );
}

export function usePOS() {
  const ctx = useContext(POSContext);
  if (!ctx) throw new Error('usePOS must be used within POSProvider');
  return ctx;
}
